/**
 * ClinicTrustSubmission — PRD1.7 Step 4B
 * Clinic submits trust data (accreditations, experience metrics) + supporting documents.
 * Route: /clinic/trust
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import {
  ArrowLeft, Shield, Upload, CheckCircle2,
  Clock, XCircle, Loader2, Award, Activity
} from 'lucide-react';
import api from '../../utils/api';
import { useRBAC } from '../../context/RBACContext';

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const styles = {
    verified: 'bg-emerald-100 text-emerald-700',
    pending_verification: 'bg-amber-100 text-amber-700',
    pending: 'bg-amber-100 text-amber-700',
    unverified: 'bg-slate-100 text-slate-600',
    rejected: 'bg-red-100 text-red-700',
  };
  const icons = {
    verified: <CheckCircle2 className="h-3 w-3 mr-1" />,
    pending_verification: <Clock className="h-3 w-3 mr-1" />,
    pending: <Clock className="h-3 w-3 mr-1" />,
    rejected: <XCircle className="h-3 w-3 mr-1" />,
  };
  const key = status || 'unverified';
  const label = t(`provider.trust.status.${key}`, { defaultValue: key.replace(/_/g, ' ') });
  return (
    <Badge className={`text-xs ${styles[status] || styles.unverified}`} data-testid={`status-${status}`}>
      {icons[status] || null}
      {label}
    </Badge>
  );
};

export default function ClinicTrustSubmission() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeClinicId } = useRBAC();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [verifiedFields, setVerifiedFields] = useState({});

  const [form, setForm] = useState({
    years_of_experience: '',
    procedures_performed: '',
    international_patient_volume: '',
    accreditations: [],
  });

  // TREATX-PROVIDER-ACCRED-CANONICAL-01: accreditation choices come from the
  // SAME canonical master Patient Search uses (GET /api/public/master/accreditations).
  // No hard-coded provider list. `slug` is the canonical identity carried on
  // the API; the human label is display-only.
  const [accOptions, setAccOptions] = useState([]);
  const [selectedAccSlug, setSelectedAccSlug] = useState('');
  const [accValidUntil, setAccValidUntil] = useState('');
  const [sessionUploads, setSessionUploads] = useState([]);
  // TREATX-ACCRED-SHORTLABEL-SEARCH-01 — clinics look for "JCI", "AACI",
  // "MoH", "Temos"; the master's long_label often starts somewhere else
  // ("Joint Commission International"), so the native <select> typeahead
  // could not find them. Free-text filter over short label, long label and
  // the canonical slug. The slug remains the only value ever submitted.
  const [accQuery, setAccQuery] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const metricsRes = await api.get('/clinic/trust-metrics');
      const m = metricsRes.data?.metrics;
      if (m) {
        setMetrics(m);
        setForm({
          years_of_experience: m.years_of_experience || '',
          procedures_performed: m.procedures_performed || '',
          international_patient_volume: m.international_patient_volume || '',
          accreditations: (m.accreditations || []).map(a => ({
            id: a.id,
            type: a.accreditation_type || 'other',
            certificate_id: a.certificate_id || '',
            status: a.verification_status || 'unverified',
          })),
        });
      }
      // TREATX-ACCRED-PROVIDER-VISIBILITY-01 — the clinic id used to come from
      // a `localStorage.clinicToken` JWT, but auth has been cookie-only since
      // FIND-H-08, so the token is always empty and this hydration never ran:
      // a clinic saw "no certificates uploaded yet" even for a certificate it
      // had uploaded and TreatX had already verified. The canonical active
      // clinic now comes from the RBAC context.
      if (activeClinicId) {
        try {
          const vfRes = await api.get(`/public/clinics/${activeClinicId}/verified-fields`);
          setVerifiedFields(vfRes.data?.verified_fields || {});
        } catch (e) { /* non-blocking: statuses stay unverified */ }
      }
    } catch (err) {
      if (err?.response?.status !== 401) {
        toast({ title: t('provider.trust.toasts.loadErrorTitle'), description: t('provider.trust.toasts.loadError'), variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, [t, activeClinicId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load the canonical accreditation master (same SSOT as Patient Search).
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/public/master/accreditations');
        const opts = (r.data?.accreditations || [])
          .filter((a) => a.is_active !== false && a.slug)
          .map((a) => ({
            slug: a.slug,
            label: a.long_label || a.name || a.short_label || a.slug,
            short: a.short_label || '',
            requiresExpiry: a.requires_expiry === true,
          }))
          // de-dupe canonical slugs (master may carry duplicate rows)
          .filter((a, i, arr) => arr.findIndex((x) => x.slug === a.slug) === i)
          .sort((a, b) => a.label.localeCompare(b.label));
        setAccOptions(opts);
      } catch (e) {
        toast({ title: t('provider.trust.accreditations.canonicalTitle'), description: t('provider.trust.accreditations.loadOptionsError'), variant: 'destructive' });
      }
    })();
  }, [t]);

  // TREATX-ACCRED-SHORTLABEL-SEARCH-01 — match on short label, long label or
  // canonical slug, ignoring spaces, dashes and case ("iso9001" finds
  // "ISO 9001"). The currently selected option is always kept in the list so
  // filtering can never silently drop a live selection.
  const visibleAccOptions = React.useMemo(() => {
    const q = accQuery.trim().toLowerCase().replace(/[\s-]/g, '');
    if (!q) return accOptions;
    const squash = (s) => String(s || '').toLowerCase().replace(/[\s-]/g, '');
    return accOptions.filter(
      (o) => o.slug === selectedAccSlug
        || squash(o.short).includes(q)
        || squash(o.label).includes(q)
        || squash(o.slug).includes(q),
    );
  }, [accOptions, accQuery, selectedAccSlug]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        years_of_experience: parseInt(form.years_of_experience) || 0,
        procedures_performed: parseInt(form.procedures_performed) || 0,
        international_patient_volume: parseInt(form.international_patient_volume) || 0,
      };
      await api.post('/clinic/trust-metrics', payload, {});
      toast({ title: t('provider.trust.toasts.submittedTitle'), description: t('provider.trust.toasts.submittedDesc') });
      fetchData();
    } catch (err) {
      toast({ title: t('provider.trust.toasts.loadErrorTitle'), description: err?.response?.data?.detail || t('provider.trust.toasts.submitFailed'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (e, docType, extra = {}) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (const f of files) formData.append('files', f);
      formData.append('document_type', docType);
      if (extra.accreditation_slug) formData.append('accreditation_slug', extra.accreditation_slug);
      if (extra.expiry_date) formData.append('expiry_date', extra.expiry_date);
      await api.post('/clinic/trust-metrics/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: t('provider.trust.toasts.uploadedTitle'), description: t('provider.trust.toasts.uploadedDesc') });
      fetchData();
    } catch (err) {
      toast({ title: t('provider.trust.toasts.uploadFailedTitle'), description: err?.response?.data?.detail || err?.response?.data?.message || t('provider.trust.toasts.uploadFailedDesc'), variant: 'destructive' });
      throw err;
    } finally {
      setUploading(false);
    }
  };

  // TREATX-PROVIDER-ACCRED-CANONICAL-01: upload a certificate bound to the
  // selected canonical accreditation. Select → upload → pending verification.
  const handleAccreditationUpload = async (e) => {
    if (!selectedAccSlug) {
      toast({ title: t('provider.trust.accreditations.canonicalTitle'), description: t('provider.trust.accreditations.chooseFirst'), variant: 'destructive' });
      e.target.value = '';
      return;
    }
    const opt = accOptions.find((o) => o.slug === selectedAccSlug) || {};
    if (opt.requiresExpiry && !accValidUntil) {
      toast({ title: opt.label || selectedAccSlug, description: t('provider.trust.accreditations.expiryRequiredHint'), variant: 'destructive' });
      e.target.value = '';
      return;
    }
    const label = opt.label || selectedAccSlug;
    try {
      await handleUploadDocument(e, 'accreditation', {
        accreditation_slug: selectedAccSlug,
        expiry_date: accValidUntil || undefined,
      });
      setSessionUploads((prev) => [...prev, { slug: selectedAccSlug, label }]);
      setSelectedAccSlug('');
      setAccValidUntil('');
      toast({ title: label, description: t('provider.trust.accreditations.submittedForReview') });
    } catch (_e) { /* toast already shown */ } finally {
      e.target.value = '';
    }
  };

  const getFieldStatus = (fieldName) => {
    const vf = verifiedFields[fieldName];
    if (!vf) return 'unverified';
    return vf.status || 'unverified';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="clinic-trust-page">
      <Toaster />
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('provider.trust.back')}
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              {t('provider.trust.title')}
            </h1>
            <p className="text-xs text-gray-500">{t('provider.trust.subtitle')}</p>
          </div>
          {metrics?.verification_level && (
            <StatusBadge status={metrics.verification_level} />
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <Card data-testid="experience-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-600" />
              {t('provider.trust.experience.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="yoe" className="text-sm">{t('provider.trust.experience.yoe')}</Label>
                  <StatusBadge status={getFieldStatus('years_of_experience')} />
                </div>
                <Input id="yoe" type="number" value={form.years_of_experience}
                  onChange={e => setForm(prev => ({ ...prev, years_of_experience: e.target.value }))}
                  data-testid="input-yoe" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="procedures" className="text-sm">{t('provider.trust.experience.procedures')}</Label>
                  <StatusBadge status={getFieldStatus('procedures_performed')} />
                </div>
                <Input id="procedures" type="number" value={form.procedures_performed}
                  onChange={e => setForm(prev => ({ ...prev, procedures_performed: e.target.value }))}
                  data-testid="input-procedures" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="intl" className="text-sm">{t('provider.trust.experience.intlPatients')}</Label>
                  <StatusBadge status={getFieldStatus('international_patient_volume')} />
                </div>
                <Input id="intl" type="number" value={form.international_patient_volume}
                  onChange={e => setForm(prev => ({ ...prev, international_patient_volume: e.target.value }))}
                  data-testid="input-intl-patients" />
              </div>
            </div>

            <div className="pt-2 border-t">
              <Label className="text-sm font-medium mb-2 block">{t('provider.trust.experience.supportingDocs')}</Label>
              <div className="flex items-center gap-3">
                <label className="cursor-pointer">
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" multiple
                    onChange={e => handleUploadDocument(e, 'experience_proof')}
                    data-testid="upload-experience-doc" />
                  <div className="flex items-center gap-2 text-sm text-blue-600 border border-blue-200 rounded-lg px-3 py-2 hover:bg-blue-50">
                    <Upload className="h-4 w-4" />
                    {uploading ? t('provider.trust.experience.uploading') : t('provider.trust.experience.uploadProof')}
                  </div>
                </label>
                <span className="text-xs text-gray-400">{t('provider.trust.experience.accepted')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="accreditations-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Award className="h-4 w-4 text-amber-600" />
              {t('provider.trust.accreditations.canonicalTitle')}
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">{t('provider.trust.accreditations.canonicalHelp')}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Select canonical accreditation → upload its certificate */}
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <Label className="text-xs">{t('provider.trust.accreditations.selectLabel')}</Label>
                <Input
                  type="text"
                  className="mb-2"
                  value={accQuery}
                  onChange={(e) => setAccQuery(e.target.value)}
                  placeholder={t('provider.trust.accreditations.searchPlaceholder')}
                  data-testid="acc-canonical-search"
                />
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={selectedAccSlug}
                  onChange={(e) => setSelectedAccSlug(e.target.value)}
                  data-testid="acc-canonical-select"
                >
                  <option value="">{t('provider.trust.accreditations.selectPlaceholder')}</option>
                  {visibleAccOptions.map((o) => (
                    <option key={o.slug} value={o.slug}>
                      {/* Prefix the code only when the long label does not
                          already start with it, so "ISO 9001 — Quality …"
                          is not rendered as "ISO 9001 — ISO 9001 — Quality …". */}
                      {o.short && !o.label.toLowerCase().startsWith(o.short.toLowerCase())
                        ? `${o.short} — ${o.label}`
                        : o.label}
                    </option>
                  ))}
                </select>
                {accQuery.trim() && visibleAccOptions.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1" data-testid="acc-canonical-no-match">
                    {t('provider.trust.accreditations.searchNoMatch')}
                  </p>
                )}
              </div>
              <div className="w-full sm:w-48">
                <Label className="text-xs">
                  {(() => {
                    const opt = accOptions.find((o) => o.slug === selectedAccSlug);
                    return opt?.requiresExpiry
                      ? t('provider.trust.accreditations.validUntilRequired')
                      : t('provider.trust.accreditations.validUntil');
                  })()}
                </Label>
                <Input
                  type="date"
                  value={accValidUntil}
                  onChange={(e) => setAccValidUntil(e.target.value)}
                  data-testid="acc-valid-until"
                />
              </div>
              {(() => {
                const opt = accOptions.find((o) => o.slug === selectedAccSlug);
                const needExpiry = !!opt?.requiresExpiry;
                const blocked = !selectedAccSlug || uploading || (needExpiry && !accValidUntil);
                return (
                  <label className={`cursor-pointer ${blocked ? 'opacity-50 pointer-events-none' : ''}`}>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleAccreditationUpload}
                      data-testid="upload-accreditation-doc"
                      disabled={blocked}
                    />
                    <div className="flex items-center gap-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-3 py-2 w-fit">
                      <Upload className="h-4 w-4" />
                      {uploading ? t('provider.trust.accreditations.uploadingCert') : t('provider.trust.accreditations.uploadCertificate')}
                    </div>
                  </label>
                );
              })()}
            </div>
            {(() => {
              const opt = accOptions.find((o) => o.slug === selectedAccSlug);
              return opt?.requiresExpiry ? (
                <p className="text-xs text-amber-700" data-testid="acc-expiry-required-hint">
                  {t('provider.trust.accreditations.expiryRequiredHint')}
                </p>
              ) : null;
            })()}

            {/* Provider's accreditation certificates + status */}
            <div className="pt-2 border-t">
              <Label className="text-sm font-medium mb-2 block">{t('provider.trust.accreditations.evidenceTitle')}</Label>
              {(() => {
                const persisted = (verifiedFields.accreditations || []).map((a) => ({
                  slug: a.name,
                  label: (accOptions.find((o) => o.slug === a.name) || {}).label || a.name,
                  status: a.status || 'pending',
                }));
                const persistedSlugs = new Set(persisted.map((p) => p.slug));
                const pending = sessionUploads
                  .filter((u) => !persistedSlugs.has(u.slug))
                  .map((u) => ({ slug: u.slug, label: u.label, status: 'pending' }));
                const rows = [...persisted, ...pending];
                if (rows.length === 0) {
                  return <p className="text-xs text-gray-400" data-testid="acc-evidence-empty">{t('provider.trust.accreditations.noEvidence')}</p>;
                }
                return (
                  <div className="space-y-2" data-testid="acc-evidence-list">
                    {rows.map((r, i) => (
                      <div key={r.slug || i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`acc-evidence-${r.slug}`}>
                        <Award className="h-5 w-5 text-amber-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{r.label}</p>
                          {r.status !== 'verified' && r.status !== 'rejected' && (
                            <p className="text-xs text-gray-500">{t('provider.trust.accreditations.statusPendingHint')}</p>
                          )}
                        </div>
                        <StatusBadge status={r.status} />
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate(-1)}>{t('provider.trust.actions.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="submit-trust-btn">
            {saving
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('provider.trust.actions.submitting')}</>
              : <><Shield className="h-4 w-4 mr-2" />{t('provider.trust.actions.submit')}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
